import * as CryptoJS from 'crypto-es';

export type ClientConfig = {
  loginUrl: string;
  apiUrl: string;
  clientId: string;
  redirectUrl: string;
  debugToken?: string;
};

export type ExchangeTokenRequest = {
  grant_type: string;
  code: string;
  redirect_uri: string;
  client_id: string;
  code_verifier: string;
};

export type TokenPairResponse = {
  token_type: string;
  access_token: string;
  expires_at: string;
  refresh_token: string;
};

export type RefreshTokenRequest = {
  refreshToken: string;
  clientId: string;
};

export type AccessTokenPayload = {
  scopes: string[];
  sub: string;
  clientId: string;
  iat: number;
  exp: number;
};

export const authCodeExchangeUrl = (apiUrl = '/api') => `${apiUrl}/tokens`;
export const refreshTokenUrl = (apiUrl = '/api') => `${apiUrl}/tokens/refresh`;

export const supportedLanguages = ['en', 'de'] as const;

export function thdsAuthCommon(): string {
  return 'thds-auth-common';
}

export function generatePKCE(length = 128) {
  if (length > 128) {
    throw new Error('The verifier can not be longer then 128 chars');
  }
  if (length < 43) {
    throw new Error('The verifier has to be at least 43 chars long');
  }

  const codeVerifier = CryptoJS.default.lib.WordArray.random(length).toString(
    CryptoJS.default.enc.Hex,
  );

  // Hash the verifier to get the challenge
  const codeChallenge = CryptoJS.default
    .SHA256(codeVerifier)
    .toString(CryptoJS.default.enc.Hex);

  const state = CryptoJS.default.lib.WordArray.random(24).toString(
    CryptoJS.default.enc.Hex,
  );

  return { codeVerifier, codeChallenge, state };
}

export function generateLoginUrl(
  { loginUrl, clientId, redirectUrl }: ClientConfig,
  scopes: string[],
  state: string,
  codeChallenge: string,
  isMobile = false,
) {
  const scopesString = serializeScopes(scopes);

  let language = document.defaultView?.navigator?.language?.slice(0, 2);

  if (
    !language ||
    !(supportedLanguages as unknown as Readonly<string>).includes(language)
  ) {
    language = 'en';
  }

  const languageLoginUrl = loginUrl.replace('/auth', `/${language}/auth`);

  let url =
    `${languageLoginUrl}?` +
    'response_type=code' +
    `&client_id=${clientId}` +
    `&redirect_uri=${redirectUrl}` +
    `&scope=${scopesString}` +
    `&state=${state}` +
    `&code_challenge=${codeChallenge}` +
    '&code_challenge_method=S256';

  if (isMobile) {
    url += '&mobile=true';
  }

  return url;
}

/**
 * Converts an array of scopes to a ; delimited string of scopes
 * @param scopes Array of scopes
 */
function serializeScopes(scopes: string[]): string {
  return scopes.join(';');
}
